import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../services/api';
import { toast } from 'sonner';
import { useFeatures } from '../../context/FeatureContext';
import {
  ClipboardList,
  Plus,
  DollarSign,
  Pause,
  Play,
  XCircle,
  CheckCircle,
  Clock,
  AlertCircle,
  CalendarDays,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import ParentRequestTimeline from '../../components/parent/ParentRequestTimeline';

import { sortPrograms } from '../../utils/programSort';

const ParentEnrollments = () => {
  const { isFeatureEnabled } = useFeatures();
  const [enrollments, setEnrollments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Enrollment Dialog
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedPaymentFrequency, setSelectedPaymentFrequency] = useState('monthly');
  
  // Action dialogs
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  
  // Form data
  const [pauseWeeks, setPauseWeeks] = useState(2);
  const [pauseReason, setPauseReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  
  // Schedule request form data
  const [scheduleRequestData, setScheduleRequestData] = useState({
    original_date: '',
    alternate_date: '',
    alternate_class_id: '',
    reason: ''
  });
  const [availableClasses, setAvailableClasses] = useState([]);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [enrollmentsRes, playersRes, programsRes, requestsRes] = await Promise.all([
        api.get('/enrollments/my-enrollments'),
        api.get('/players'),
        api.get('/programs'),
        api.get('/enrollments/requests/my-requests').catch(() => ({ data: [] }))
      ]);
      setEnrollments(enrollmentsRes.data);
      setPlayers(playersRes.data);
      setPrograms(sortPrograms(programsRes.data.filter(p => p.is_active)));
      setMyRequests(requestsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEnrollRequest = async (e) => {
    e.preventDefault();
    if (!selectedPlayer || !selectedProgram) {
      toast.error('Please select a player and program');
      return;
    }
    try {
      await api.post('/enrollments', {
        player_id: selectedPlayer,
        program_id: selectedProgram,
        payment_frequency: selectedPaymentFrequency
      });
      toast.success('Enrollment request submitted');
      setEnrollDialogOpen(false);
      setSelectedPlayer('');
      setSelectedProgram('');
      setSelectedPaymentFrequency('monthly');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit enrollment');
    }
  };

  const handlePayment = async (enrollmentId) => {
    try {
      const enrollment = enrollments.find(e => e.id === enrollmentId);
      
      if (!enrollment) {
        toast.error('Enrollment not found');
        return;
      }
      
      const program = programs.find(p => p.id === enrollment.program_id);
      const isFixedProgram = program?.program_type === 'fixed_duration';
      
      if (isFixedProgram) {
        const response = await api.post(`/billing/payment/fixed-program?enrollment_id=${enrollmentId}&origin_url=${encodeURIComponent(window.location.origin)}`);
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } else {
        const response = await api.post('/billing/enrollment/create', {
          enrollment_id: enrollmentId,
          payment_frequency: enrollment.payment_frequency || 'monthly',
          origin_url: window.location.origin
        });
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handlePause = async () => {
    if (!selectedEnrollment || !pauseReason.trim()) {
      toast.error('Please provide a reason for the pause request');
      return;
    }
    try {
      await api.post(`/enrollments/${selectedEnrollment.id}/request-pause`, {
        pause_weeks: parseInt(pauseWeeks),
        reason: pauseReason
      });
      toast.success('Pause request submitted for admin approval');
      setPauseDialogOpen(false);
      setSelectedEnrollment(null);
      setPauseWeeks(2);
      setPauseReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit pause request');
    }
  };

  const handleResume = async () => {
    if (!selectedEnrollment) return;
    try {
      await api.post(`/enrollments/${selectedEnrollment.id}/request-resume`);
      toast.success('Resume request submitted for admin approval');
      setResumeDialogOpen(false);
      setSelectedEnrollment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit resume request');
    }
  };

  const handleCancel = async () => {
    if (!selectedEnrollment || !cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    try {
      await api.post(`/enrollments/${selectedEnrollment.id}/request-cancel`, {
        reason: cancelReason
      });
      toast.success('Cancellation request submitted. 30-day notice period applies.');
      setCancelDialogOpen(false);
      setSelectedEnrollment(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit cancellation request');
    }
  };

  const openScheduleDialog = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    setScheduleRequestData({
      original_date: '',
      alternate_date: '',
      alternate_class_id: '',
      reason: ''
    });
    
    // Fetch classes for the same program
    try {
      const response = await api.get(`/classes?program_id=${enrollment.program_id}`);
      // Filter out the current class
      const otherClasses = response.data.filter(c => c.id !== enrollment.class_id);
      setAvailableClasses(otherClasses);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      setAvailableClasses([]);
    }
    
    setScheduleDialogOpen(true);
  };

  const handleScheduleRequest = async () => {
    if (!selectedEnrollment) return;
    
    if (!scheduleRequestData.original_date) {
      toast.error('Please select the date you want to miss');
      return;
    }
    if (!scheduleRequestData.alternate_date) {
      toast.error('Please select an alternate date');
      return;
    }
    if (!scheduleRequestData.alternate_class_id) {
      toast.error('Please select an alternate class');
      return;
    }

    setScheduleSubmitting(true);
    try {
      await api.post('/class-schedule-requests', {
        enrollment_id: selectedEnrollment.id,
        player_id: selectedEnrollment.player_id,
        original_date: scheduleRequestData.original_date,
        original_class_id: selectedEnrollment.class_id,
        alternate_date: scheduleRequestData.alternate_date,
        alternate_class_id: scheduleRequestData.alternate_class_id,
        reason: scheduleRequestData.reason || null
      });
      toast.success('Schedule change request submitted');
      setScheduleDialogOpen(false);
      setSelectedEnrollment(null);
      setScheduleRequestData({
        original_date: '',
        alternate_date: '',
        alternate_class_id: '',
        reason: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit schedule request');
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pending Approval' },
      approved: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Approved' },
      current: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Active' },
      pause_requested: { color: 'bg-orange-100 text-orange-700', icon: Clock, label: 'Pause Requested' },
      paused: { color: 'bg-slate-100 text-slate-700', icon: Pause, label: 'Paused' },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
      canceled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' },
      cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' }
    };
    const statusConfig = config[status] || { color: 'bg-slate-100 text-slate-600', icon: AlertCircle, label: status };
    const Icon = statusConfig.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        <Icon className="h-3 w-3" />
        {statusConfig.label}
      </span>
    );
  };

  const filteredEnrollments = enrollments.filter(e => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return ['approved', 'current'].includes(e.status);
    if (statusFilter === 'paused') return ['paused', 'pause_requested'].includes(e.status);
    if (statusFilter === 'cancelled') return ['canceled', 'cancelled', 'rejected'].includes(e.status);
    return e.status === statusFilter;
  });

  // Show Pay Now for any non-settled status: pending (active checkout / future),
  // unpaid (no Stripe session, money still owed), or overdue. Excludes paid /
  // refunded / void / cancelled.
  const pendingPayments = enrollments.filter(
    e => e.status === 'approved' && ['pending', 'unpaid', 'overdue'].includes(String(e.payment_status || '').toLowerCase())
  );

  const rejectedRequests = myRequests.filter(r => r.status === 'rejected');

  if (loading) {
    return (
      <DashboardLayout title="Enrollments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Enrollments">
      <div className="space-y-6" data-testid="enrollments-page">
        {/* Pending Payments Alert */}
        {pendingPayments.length > 0 && (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-amber-800 flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Pending Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingPayments.map((enrollment) => (
                  <div 
                    key={enrollment.id}
                    className="flex items-center justify-between bg-white p-4 rounded-lg"
                    data-testid={`pending-payment-${enrollment.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{enrollment.player_name}</p>
                      <p className="text-sm text-slate-500">{enrollment.program_name}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="font-semibold text-slate-900">
                        {cs()}{(enrollment.fee_amount || enrollment.monthly_fee || 0).toFixed(2)}
                      </p>
                      <Button
                        onClick={() => handlePayment(enrollment.id)}
                        className="bg-amber-500 hover:bg-amber-600"
                        data-testid={`pay-now-${enrollment.id}`}
                      >
                        Pay Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter318 wave 10 — Parent Request Timeline. Chronological
            view of pause/resume/cancel requests. Hides itself when
            the parent has no request history so the tab stays clean. */}
        <ParentRequestTimeline
          requests={myRequests}
          enrollmentById={Object.fromEntries(enrollments.map((e) => [e.id, e]))}
        />

        {/* Enrollments List - My Enrollments on top */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="font-heading text-lg lg:text-xl text-amber-800 flex items-center gap-2 whitespace-nowrap">My Enrollments</CardTitle>
              <div className="flex items-center gap-2">
                {/* Status Filter */}
                <div className="flex gap-0.5 bg-slate-100 p-0.5 rounded-lg overflow-x-auto">
                  {['all', 'active', 'paused', 'cancelled'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setStatusFilter(filter)}
                      className={`px-2 lg:px-3 py-1 text-xs lg:text-sm rounded-md transition-colors capitalize whitespace-nowrap ${
                        statusFilter === filter 
                          ? 'bg-white text-slate-900 shadow-sm' 
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800 whitespace-nowrap" data-testid="new-enrollment-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">New Enrollment</span>
                      <span className="sm:hidden">New</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-xl">Request Enrollment</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEnrollRequest} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Select Child</Label>
                        <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
                          <SelectTrigger data-testid="enroll-player-select">
                            <SelectValue placeholder="Choose a child" />
                          </SelectTrigger>
                          <SelectContent>
                            {players.map((player) => (
                              <SelectItem key={player.id} value={player.id}>
                                {player.full_name || player.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Select Program</Label>
                        <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                          <SelectTrigger data-testid="enroll-program-select">
                            <SelectValue placeholder="Choose a program" />
                          </SelectTrigger>
                          <SelectContent>
                            {programs.map((program) => (
                              <SelectItem key={program.id} value={program.id}>
                                {program.name} - {cs()}{program.price}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Payment Frequency</Label>
                        <Select value={selectedPaymentFrequency} onValueChange={setSelectedPaymentFrequency}>
                          <SelectTrigger data-testid="enroll-payment-frequency-select">
                            <SelectValue placeholder="Choose payment frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly (3% discount)</SelectItem>
                            <SelectItem value="biannual">Bi-Annual (5% discount)</SelectItem>
                            <SelectItem value="one_time">One-Time Payment</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">
                          {selectedPaymentFrequency === 'quarterly' && 'Pay every 3 months and save 3%'}
                          {selectedPaymentFrequency === 'biannual' && 'Pay every 6 months and save 5%'}
                          {selectedPaymentFrequency === 'one_time' && 'Single payment for the entire program'}
                          {selectedPaymentFrequency === 'monthly' && 'Pay month-to-month'}
                        </p>
                      </div>

                      <DialogFooter className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setEnrollDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="submit-enrollment-btn">
                          Submit Request
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filteredEnrollments.length === 0 ? (
              <div className="p-12 text-center">
                <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No enrollments found</p>
                <p className="text-sm text-slate-400 mt-1">
                  {statusFilter !== 'all' ? 'Try changing the filter or ' : ''}Request an enrollment to get started
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredEnrollments.map((enrollment) => (
                  <div 
                    key={enrollment.id}
                    className="p-3 lg:p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`enrollment-${enrollment.id}`}
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm lg:text-base text-slate-900">{enrollment.player_name}</p>
                        {getStatusBadge(enrollment.status)}
                      </div>
                      <p className="text-xs lg:text-sm text-slate-500">{enrollment.program_name}</p>
                      {enrollment.class_names && enrollment.class_names.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`parent-enrollment-classes-${enrollment.id}`}>
                          {enrollment.class_names.map((name, idx) => (
                            <span
                              key={(enrollment.class_ids && enrollment.class_ids[idx]) || `${enrollment.id}-${idx}`}
                              className="text-[10px] lg:text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      ) : enrollment.class_name ? (
                        <p className="text-[10px] lg:text-xs text-slate-400">Class: {enrollment.class_name}</p>
                      ) : null}
                      {enrollment.monthly_fee && (
                        <p className="text-xs lg:text-sm font-medium text-slate-700">
                          {cs()}{enrollment.monthly_fee.toFixed(2)}/{enrollment.payment_frequency || 'month'}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {/* Show action buttons based on status */}
                        {['approved', 'current'].includes(enrollment.status) && isFeatureEnabled('pause_enrollment') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedEnrollment(enrollment);
                              setPauseDialogOpen(true);
                            }}
                            className="text-amber-600 border-amber-200 hover:bg-amber-50 h-7 text-xs lg:h-9 lg:text-sm"
                          >
                            <Pause className="h-3.5 w-3.5 mr-1 shrink-0" />
                            Pause
                          </Button>
                        )}
                        
                        {enrollment.status === 'paused' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedEnrollment(enrollment);
                              setResumeDialogOpen(true);
                            }}
                            className="text-green-600 border-green-200 hover:bg-green-50 h-7 text-xs lg:h-9 lg:text-sm"
                          >
                            <Play className="h-3.5 w-3.5 mr-1 shrink-0" />
                            Resume
                          </Button>
                        )}
                        
                        {['approved', 'current', 'paused'].includes(enrollment.status) && isFeatureEnabled('cancel_enrollment') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedEnrollment(enrollment);
                              setCancelDialogOpen(true);
                            }}
                            className="text-rose-600 border-rose-200 hover:bg-rose-50 h-7 text-xs lg:h-9 lg:text-sm"
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1 shrink-0" />
                            Cancel
                          </Button>
                        )}
                        
                        {['approved', 'current'].includes(enrollment.status) && enrollment.class_id && isFeatureEnabled('makeup_classes') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openScheduleDialog(enrollment)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50 h-7 text-xs lg:h-9 lg:text-sm"
                            data-testid={`schedule-change-${enrollment.id}`}
                          >
                            <CalendarDays className="h-3.5 w-3.5 mr-1 shrink-0" />
                            <span className="hidden sm:inline">Request Alternate Day</span>
                            <span className="sm:hidden">Alt. Day</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rejected Requests - shown after My Enrollments */}
        {rejectedRequests.length > 0 && (
          <Card className="border-2 border-rose-200 bg-rose-50">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-rose-800 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Rejected Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {rejectedRequests.map((request) => (
                  <div 
                    key={request.id}
                    className="bg-white p-4 rounded-lg border border-rose-100"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{request.player_name}</p>
                        <p className="text-sm text-slate-500 capitalize">
                          {request.type?.replace('_', ' ')} Request
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-rose-100 text-rose-700 text-xs rounded-full">
                        Rejected
                      </span>
                    </div>
                    {request.rejection_reason && (
                      <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">
                        <strong>Reason:</strong> {request.rejection_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pause Dialog */}
        <Dialog open={pauseDialogOpen} onOpenChange={(open) => {
          setPauseDialogOpen(open);
          if (!open) {
            setSelectedEnrollment(null);
            setPauseWeeks(2);
            setPauseReason('');
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Pause className="h-5 w-5 text-amber-500" />
                Request Pause
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
                <p className="text-sm text-slate-500">{selectedEnrollment?.program_name}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Pause Duration</Label>
                <Select value={String(pauseWeeks)} onValueChange={(v) => setPauseWeeks(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 Weeks</SelectItem>
                    <SelectItem value="3">3 Weeks</SelectItem>
                    <SelectItem value="4">4 Weeks</SelectItem>
                    <SelectItem value="6">6 Weeks</SelectItem>
                    <SelectItem value="8">8 Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-amber-600">Reason *</Label>
                <Textarea
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder="Please explain why you need to pause..."
                  rows={3}
                />
              </div>
              
              <DialogFooter className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setPauseDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePause}
                  disabled={!pauseReason.trim()}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  Submit Pause Request
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Resume Dialog */}
        <Dialog open={resumeDialogOpen} onOpenChange={(open) => {
          setResumeDialogOpen(open);
          if (!open) setSelectedEnrollment(null);
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Play className="h-5 w-5 text-green-500" />
                Request Resume
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
                <p className="text-sm text-slate-500">{selectedEnrollment?.program_name}</p>
              </div>
              
              <p className="text-sm text-slate-600">
                Are you ready to resume this enrollment? A request will be sent to the admin for approval.
              </p>
              
              <DialogFooter className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setResumeDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleResume} className="bg-green-500 hover:bg-green-600">
                  Submit Resume Request
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) {
            setSelectedEnrollment(null);
            setCancelReason('');
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <XCircle className="h-5 w-5 text-rose-500" />
                Request Cancellation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
                <p className="text-sm text-slate-500">{selectedEnrollment?.program_name}</p>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> A 30-day notice period applies to all cancellations.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-rose-600">Reason for Cancellation *</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please explain your reason for cancellation..."
                  rows={3}
                />
              </div>
              
              <DialogFooter className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                  Keep Enrollment
                </Button>
                <Button 
                  onClick={handleCancel}
                  disabled={!cancelReason.trim()}
                  className="bg-rose-500 hover:bg-rose-600"
                >
                  Submit Cancellation
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Schedule Change Request Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={(open) => {
          setScheduleDialogOpen(open);
          if (!open) {
            setSelectedEnrollment(null);
            setScheduleRequestData({
              original_date: '',
              alternate_date: '',
              alternate_class_id: '',
              reason: ''
            });
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-blue-500" />
                Request Alternate Class Day
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
                <p className="text-sm text-slate-500">{selectedEnrollment?.program_name}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Current: {selectedEnrollment?.class_name} ({selectedEnrollment?.class_day} {selectedEnrollment?.class_time})
                </p>
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Use this to request an alternate class day when you have a scheduling conflict. 
                  Admin approval is required.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Date to Miss *</Label>
                <Input
                  type="date"
                  value={scheduleRequestData.original_date}
                  onChange={(e) => setScheduleRequestData({
                    ...scheduleRequestData,
                    original_date: e.target.value
                  })}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="original-date-input"
                />
                <p className="text-xs text-slate-500">The date you cannot attend your regular class</p>
              </div>

              <div className="space-y-2">
                <Label>Alternate Date *</Label>
                <Input
                  type="date"
                  value={scheduleRequestData.alternate_date}
                  onChange={(e) => setScheduleRequestData({
                    ...scheduleRequestData,
                    alternate_date: e.target.value
                  })}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="alternate-date-input"
                />
                <p className="text-xs text-slate-500">The date you would like to attend instead</p>
              </div>

              <div className="space-y-2">
                <Label>Alternate Class *</Label>
                <Select
                  value={scheduleRequestData.alternate_class_id}
                  onValueChange={(value) => setScheduleRequestData({
                    ...scheduleRequestData,
                    alternate_class_id: value
                  })}
                >
                  <SelectTrigger data-testid="alternate-class-select">
                    <SelectValue placeholder="Select an alternate class" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableClasses.length === 0 ? (
                      <div className="px-2 py-3 text-center text-sm text-slate-500">
                        No other classes available
                      </div>
                    ) : (
                      availableClasses.map((cls) => (
                        <SelectItem key={cls.id} value={cls.id}>
                          {cls.name} - {cls.schedule_day} {cls.start_time}-{cls.end_time}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Textarea
                  value={scheduleRequestData.reason}
                  onChange={(e) => setScheduleRequestData({
                    ...scheduleRequestData,
                    reason: e.target.value
                  })}
                  placeholder="Why do you need to change this date? (optional)"
                  rows={2}
                  data-testid="schedule-reason-input"
                />
              </div>
              
              <DialogFooter className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleScheduleRequest}
                  disabled={scheduleSubmitting || !scheduleRequestData.original_date || !scheduleRequestData.alternate_date || !scheduleRequestData.alternate_class_id}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="submit-schedule-request"
                >
                  {scheduleSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentEnrollments;
